/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */

import EnvironmentInfo from '../environment-info';
import { getRestEndPoint, resolveRestApi } from '../router/app-router';
import { invokeRestApi } from '../rest-client';
import type { BuildRestAPIPayload } from '../router/types';

import Utils from './utils';
import type {
  Component,
  ComponentConfig,
  ComponentSearchCriteria,
  ComponentData,
  Asset,
  LoadAssetOptions
} from './types';

const GET_CUSTOM_COMPONENT = 'getCustomComponent';
const GET_BINARY_FILE = 'getBinaryFile';
const GET_OPERATOR_IMAGE = 'getOperatorImage';

const TEXT_JAVASCRIPT = 'text/javascript';

/**
 * @description
 * Exposes APIs to load the script and css files.
 */
class AssetLoader {
  public loaderObj: { [key: string]: Function };
  public staticServerURL: string | null;
  public appStaticUrl: string | null;
  public pegaOnlyUrl: boolean;
  public loadedAssets: { [key: string]: boolean };
  public componentsConfig: ComponentConfig[] | null;
  public appAlias: string;
  public b2sJWT: string | null;
  public ccV2LibId: string | null;
  public imageBlobUrlCache: { [key: string]: Promise<string | Blob> };
  getTypes = {
    SCRIPT: 'script',
    STYLE: 'link'
  };

  constructor() {
    this.loaderObj = {};
    this.staticServerURL = '';
    this.appStaticUrl = null;
    this.pegaOnlyUrl = false; // indicates that pega content separated from customer components
    this.loadedAssets = {};
    this.componentsConfig = null; // Pega components aggregate config
    this.appAlias = '';
    this.b2sJWT = null;
    this.ccV2LibId = '';
    this.imageBlobUrlCache = {};
  }

  /**
   * Returns the static content url
   * @description This exposes the method to fetch static content url
   *
   * @example <caption>Example for getStaticServerUrl()</caption>
   * Example can be seen in C11n getImagePath method
   * Example usage - AssetLoader.getStaticServerUrl()
   * // returns -- Static content url string
   *
   * @see {@link getStaticServerUrl} for further information.
   * @see {@link getImagePath} for example.
   * @returns the Asset Loader Object
   * @function
   */
  getStaticServerUrl() {
    return this.staticServerURL;
  }

  /**
   * This method is used to set the custom component library and version coming from DSS.
   * @description This exposes the method to set the custom component lib and version.
   * @example <caption>Example for setCCV2LibId()</caption>
   * Example can be seen in importCustomComponentMap method od AssetLoader
   * @param ccV2LibId name
   * @see {@link ccV2LibId} for further information.
   * @function
   * @private
   */
  setCCV2LibId(ccV2LibId: string) {
    this.ccV2LibId = ccV2LibId;
  }

  /**
   * Returns the ccV2LibId from custom component library and version
   * @description This exposes the method to get the custom component library and version from DSS.
   * @example <caption>Example for getCCV2LibId()</caption>
   * Example usage - Custom component v2 tools - utils.
   * // returns -- ccV2LibId string for the current request.
   * @see {@link getCCV2LibId} for further information.
   * @returns the custom component library and version
   * @function
   * @private
   */
  getCCV2LibId() {
    return this.ccV2LibId;
  }

  /**
   * Returns the theme schema json
   * @description This exposes the method to fetch theme schema json
   *
   * @example <caption>Example for getThemeSchemaJSON()</caption>
   * Example usage - AssetLoader.getThemeSchemaJSON()
   * // returns -- Theme schema json
   *
   * @see {@link getThemeSchemaJSON} for further information.
   * @returns the Asset Loader Object
   * @function
   * @private
   */
  async getThemeSchemaJSON() {
    const url = `${this.staticServerURL}themeOverrides.schema.json`;
    return fetch(url).then((res) => res.json());
  }

  /**
   * Returns the Constellation service url - no terminating /
   * @description This exposes the method to fetch the Constellation service url
   *
   * @example <caption>Example for getConstellationSErviceUrl()</caption>
   * Example can be seen in C11n getImagePath method
   * Example usage - AssetLoader.getConstellationServiceUrl()
   * // returns -- Constellation service url string
   *
   * @see {@link getConstellationServiceUrl} for further information.
   * @see {@link getImagePath} for example.
   * @returns the Asset Loader Object
   * @function
   */
  getConstellationServiceUrl() {
    return this.staticServerURL?.endsWith('/')
      ? this.staticServerURL.substring(0, this.staticServerURL.length - 1)
      : this.staticServerURL;
  }

  /**
   * This method is used to set the appAlias for the current request.
   * @description This exposes the method set the app alias for the current request.
   * @example <caption>Example for setAppAlias()</caption>
   * Example can be seen in bootstrap-utils setAppAlias method
   * @param appAlias name
   * @see {@link setAppAlias} for further information.
   * @function
   */
  setAppAlias(appAlias: string) {
    this.appAlias = appAlias;
  }

  /**
   * Returns the appAlias for the current application
   * @description This exposes the method to get the app alias for the current request.
   * @example <caption>Example for getAppAlias()</caption>
   * Example can be seen in C11n getImagePath method
   * Example usage - AssetLoader.getAppAlias()
   * // returns -- appAlias string for the current request.
   * @see {@link getAppAlias} for further information.
   * @see {@link getImagePath} for example.
   * @returns the app alias
   * @function
   */
  getAppAlias() {
    return this.appAlias;
  }

  /**
   * This exposes a method to call the Constellation service to fetch the corresponding component config.json that satisfies the specified properties.
   * This call is authenticated by the service, using the token issued for the Constellation service (B2S JWT).
   * @example <caption>Example for getSvcComponentsConfig()</caption>
   * // In this example, the API retrieves an operator image and creates a DOMString containing a URL representing the image Blob object.
   * const templateDefinitionConfigJson = await AssetLoader
   *      .getSvcComponentsConfig([{ field: 'name', value: ‘OneColumn’ }])
   *      .then((response) => {
   *         if (response?.data?.components?.[0]) {
   *           return response.data.components[0];
   *         }
   *         throw new Error('templateDefinition for OneColumn not found!');
   *      })
   *      .catch(() => {
   *         throw new Error('templateDefinition for OneColumn not found!');
   *      });
   * @param criteriaToMatch of name-value pairs specifying the search criteria
   * @param options of additional information required such as:
   *  -alternateDesignSystemURL {String} - Contains the URL for an alternative design system, used for fetching additional config-ext.json values
   * @returns - Contains an attribute data that maps to an array of config objects matching the criteria. In the event of an alternateDesignSystemURL specified in the options,
   * and only one config object is obtained via the initial fetch call, the alternative design system's config-ext.json will be embedded in an alternateDesignSystemConfig attribute within
   * the single config of the returned componentData. Errors will be stored inside the alternateDesignSystemConfig attribute as well in the case of an unsuccessful fetch.
   * @function
   */
  async getSvcComponentsConfig(
    criteriaToMatch: ComponentSearchCriteria[],
    options: { alternateDesignSystemURL?: string; skipLoadCustomerComponents?: boolean } = {}
  ): Promise<ComponentData> {
    let params = '';
    let i = 0;
    criteriaToMatch.forEach((criteria) => {
      params += `${i === 0 ? '?' : '&'}field${i}=${criteria.field}&value${i}=${criteria.value}`;
      i += 1;
    });
    // for CDN architecture, Pega and customer assets are on separate urls's
    return this.pegaOnlyUrl
      ? this.getSvcComponentsConfigSeparatedUrls(criteriaToMatch, params, options)
      : this.getSvcComponentsConfigCombinedUrl(criteriaToMatch, params, options);
  }

  // used for separate pega + app specific service content architecture
  // should always return content: [pega+customer], [pega], []. should not throw an exception - that stops authoring
  async getSvcComponentsConfigSeparatedUrls(
    criteriaToMatch: ComponentSearchCriteria[],
    params: string,
    options: { alternateDesignSystemURL?: string; skipLoadCustomerComponents?: boolean }
  ) {
    const builtOnApps = EnvironmentInfo.getBuiltOnAppNames();

    const p1 = this.loadAndFilterPegaComponentsConfig(criteriaToMatch, builtOnApps);

    let p2: Promise<any[]> = Promise.resolve([]);
    if (options.skipLoadCustomerComponents !== true) {
      p2 = this.loadAndCustomerComponentsConfig(params, criteriaToMatch);
    }
    const componentsArray: ComponentConfig[] = [];

    const infinityStaticService = EnvironmentInfo.environmentInfoObject?.pyInfinityStaticService;
    if (infinityStaticService) {
      const sdkComponentsArray = [];
      const componentConfigs = await Promise.all([p1, p2]);
      const [pegaComponentConfigs, customerAndSdkComponentConfigs] = componentConfigs;

      // Separate SDK components from customer components
      const filteredCustomerComponents = [];
      for (const config of customerAndSdkComponentConfigs) {
        if (config.isSdkOverridden === true) {
          sdkComponentsArray.push(config.componentDefinition); // Add SDK component to sdkComponentsArray
        } else {
          filteredCustomerComponents.push(config.componentDefinition); // Add non-SDK component to filteredCustomerComponents
        }
      }

      const list = this.mergeComponentList([pegaComponentConfigs, filteredCustomerComponents, sdkComponentsArray]);
      componentsArray.push(...list);
    } else {
      const sdkCompPromise = this.fetchSDKComponent(params);

      // wait for load of pega and customer component configs, and merge Pega and customer results
      await Promise.all([p1, p2, sdkCompPromise]).then((componentConfigs) => {
        const list = this.mergeComponentList(componentConfigs);
        componentsArray.push(...list);
      });
    }

    // fetch alternate design system content - Max knows the magic for length of 1 and 0 replacement
    if (options.alternateDesignSystemURL && componentsArray.length === 1) {
      const ads = await this.getSvcComponentsConfigADS(criteriaToMatch, params, options);
      componentsArray[0].alternateDesignSystemConfig = ads;
    }

    return { data: { components: componentsArray } };
  }

  mergeComponentList = (componentConfigList: [ComponentConfig[], ComponentConfig[], ComponentConfig[]]) => {
    const [pegaComponentConfigs = [], customerComponentConfigs = [], sdkComponentConfigs = []] = componentConfigList;
    const SDK_COMPONENTS_KEY = 'alternateDesignSystemConfig';
    const tempList = [...pegaComponentConfigs, ...customerComponentConfigs];

    if (this.appStaticUrl) {
      return tempList.map((config) => {
        const { name, type, subtype } = config;

        const sdkConfig = sdkComponentConfigs.reduce((carry, cfg) => {
          if (name === cfg.name && type === cfg.type && subtype === cfg.subtype) {
            carry = cfg;
          }
          return carry;
        }, {});

        if (Object.keys(sdkConfig).length > 0) {
          return { ...config, [SDK_COMPONENTS_KEY]: { ...sdkConfig } };
        }
        return config;
      });
    }
    return tempList;
  };

  async fetchSDKComponent(params: string) {
    if (this.appStaticUrl && this.appStaticUrl.length > 8 && this.appStaticUrl !== this.staticServerURL) {
      const customerComponentsConfigUrl = `${this.appStaticUrl}v102/${this.appAlias}/componentslist-ext${params}`;
      return fetch(customerComponentsConfigUrl, {
        method: 'GET',
        credentials: 'include',
        headers: { Authorization: `Bearer ${this.b2sJWT}` }
      })
        .then((response) => response.json())
        .then((data) => data.components)
        .catch(() => []);
    }
    return Promise.resolve([]);
  }

  // load and filter pega components
  async loadAndFilterPegaComponentsConfig(criteriaToMatch: ComponentSearchCriteria[], builtOnApps?: string) {
    return this.loadPegaComponentsConfig().then(() => {
      // here be magic - filter on the potentially multiple name/value clauses
      const componentsArray: ComponentConfig[] = [];
      if (this.componentsConfig) {
        this.componentsConfig.forEach((cfg) => {
          let match = true;
          criteriaToMatch.forEach((criteria) => {
            // some configs have an array in some properties, set false if non match on all
            if (Array.isArray(cfg[criteria.field])) {
              if (!(cfg[criteria.field] as string[]).includes(criteria.value)) {
                match = false;
                // break;
              }
            } else if (cfg[criteria.field] !== criteria.value) {
              match = false;
              // break;
            }
          });
          // also check if component allowed apps intersects with builtOn apps
          if (match && this.checkForBuiltOnSupport(builtOnApps, cfg.allowedApplications as string[])) {
            componentsArray.push(cfg);
          }
        });
      }
      return componentsArray;
    });
  }

  // load pega components
  async loadPegaComponentsConfig() {
    let p: Promise<void> | null = null;
    // if not already loaded, retrieve the Pega out-of-the-box component data once - store locally
    if (this.componentsConfig === null) {
      const pegaComponentsConfigUrl = `${this.staticServerURL}components/componentsconfig.json?v=${new Date().getTime()}`;
      p = fetch(pegaComponentsConfigUrl, { method: 'GET' })
        .then((response) => response.json())
        .then(async (data) => {
          this.componentsConfig = data;
          if (this.ccV2LibId) {
            await this.loadLibraryComponentsConfig(this.ccV2LibId)
              .then((libComponentConfigs) => {
                if (libComponentConfigs && libComponentConfigs.length > 0) {
                  this.componentsConfig = (this.componentsConfig || []).concat(libComponentConfigs);
                }
              })
              .catch((ex) => {
                console.error(
                  `Exception while appending custom component config from library ${this.ccV2LibId} : ${ex}`
                );
              });
          }
        })
        .catch(); // kill the bubble
    } else {
      p = Promise.resolve();
    }
    return p;
  }

  async loadLibraryComponentsConfig(libId: string) {
    let p: Promise<any> | null = null;
    p = fetch(`${this.appStaticUrl}v100/componentlib/componentsconfig/${libId}?v=${Date.now()}`, {
      headers: { Authorization: `Bearer ${this.b2sJWT}` }
    })
      .then((response) => {
        if (!response.ok) {
          console.error('Error while fetching custom component config/library component config');
          throw new Error(response.statusText);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => Promise.resolve(data))
      .catch((ex) => {
        console.error(`Exception while fetching custom component config from library ${libId} : ${ex}`);
        return { error: ex.message };
      });
    return p;
  }

  // fetch customer component config from app-static svc
  async loadAndCustomerComponentsConfig(params: string, criteriaToMatch: ComponentSearchCriteria[]) {
    let p = null;
    const infinityStaticService = EnvironmentInfo.environmentInfoObject?.pyInfinityStaticService;
    if (infinityStaticService) {
      const dataPageParams: { [key: string]: string } = {};
      criteriaToMatch.forEach((item, index) => {
        dataPageParams[`field${index}`] = item.field;
        dataPageParams[`value${index}`] = item.value;
      });

      p = PCore.getDataPageUtils()
        .getDataAsync('D_pzGetComponentsConfig', '', dataPageParams)
        .then((response) => {
          return response.data.map((item) => {
            const componentDefinition = JSON.parse(item.pxComponentDefinition);
            const isSdkOverridden = item.pyIsSDKOverridden !== undefined ? item.pyIsSDKOverridden : false;
            return {
              componentDefinition,
              isSdkOverridden
            };
          });
        });
    } else if (this.appStaticUrl && this.appStaticUrl.length > 8 && this.appStaticUrl !== this.staticServerURL) {
      const customerComponentsConfigUrl = `${this.appStaticUrl}v103/${this.appAlias}/componentslist${params}`;
      p = fetch(customerComponentsConfigUrl, {
        method: 'GET',
        credentials: 'include',
        headers: { Authorization: `Bearer ${this.b2sJWT}` }
      })
        .then((response) => response.json())
        .then((data) => data.components)
        .catch(() => []);
    } else {
      p = Promise.resolve([]);
    }
    return p;
  }

  checkForBuiltOnSupport(builtOnApps: string | undefined, allowedApplicationsFromConfig: string[]) {
    // default to allow
    if (
      !builtOnApps ||
      !allowedApplicationsFromConfig ||
      builtOnApps.length === 0 ||
      allowedApplicationsFromConfig.length === 0
    ) {
      return true;
    }
    const boa = builtOnApps.split(',');
    if (boa.length === 0) {
      return true;
    }

    return boa.some((app) => {
      return allowedApplicationsFromConfig.includes(app);
    });
  }

  // used for pre (CDN+service) architecture
  async getSvcComponentsConfigCombinedUrl(
    criteriaToMatch: ComponentSearchCriteria[],
    params: string,
    options: { alternateDesignSystemURL?: string; skipLoadCustomerComponents?: boolean }
  ): Promise<ComponentData> {
    /*
     * Commented out the following call to serviceBrokerFetch() due to BUG-635925. Also note that ServiceBrokerFetch seems to
     * rely on "context" and "pzctkn" request headers when called from Infinity (see comment in httpmain.js), yet don't appear necessary
     * when invoked from Constellation (i.e., UI Authoring). Because serviceBrokerFetch is Axios-based, return out the data in an object
     * to mimic it (so the corresponding calls to this API do not need to change and serviceBrokerFetch() could be swapped in).
     */
    const componentCallURL = `${this.staticServerURL}v102/${this.appAlias}/componentslist${params}`;

    // Retrieve the out-of-the-box component data
    const componentData = await fetch(componentCallURL, {
      method: 'GET',
      credentials: 'include',
      headers: { Authorization: `Bearer ${this.b2sJWT}` }
    })
      .then((response) => response.json())
      .then((data) => {
        return { data };
      });

    // handle filtering out customer components if option is passed to do so
    if (options.skipLoadCustomerComponents === true && componentData?.data?.components?.length > 0) {
      componentData.data.components = componentData.data.components.filter((component: Component) => {
        return component.componentKey === undefined;
      });
    }

    // US-442670: If an alternative design system is provided, and we only have one component in our componentData (i.e., single template / scalar field),
    // fetch the additional config.json settings.
    if (options.alternateDesignSystemURL && componentData?.data?.components?.length === 1) {
      const ads = await this.getSvcComponentsConfigADS(criteriaToMatch, params, options);
      componentData.data.components[0].alternateDesignSystemConfig = ads;
    }

    return componentData;
  }

  getSvcComponentsConfigADS(
    criteriaToMatch: ComponentSearchCriteria[],
    params: string,
    options: { alternateDesignSystemURL?: string }
  ) {
    const alternateDesignSystemCallURL = `${options.alternateDesignSystemURL}${options.alternateDesignSystemURL?.endsWith('/') ? '' : '/'}components/${criteriaToMatch[0].value}/config-ext.json`;

    // Retrieve the alternate design system component data
    return fetch(alternateDesignSystemCallURL, {
      method: 'GET'
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(response.statusText);
      })
      .catch((error) => {
        // In the event that the fetch was unsuccessful, place an error inside the returned componentData's alternateDesignSystemConfig attribute
        return { error: error.message };
      });
  }

  /**
   * This exposes a method to call the Constellation service to fetch an image. This call is authenticated
   * by the service, using the token issued for the Constellation service (B2S JWT).
   * @example <caption>Example for getSvcImage()</caption>
   * // In this example, the API retrieves an operator image and creates a DOMString containing a URL representing the image Blob object.
   * const operatorImg = await AssetLoader
   *       .getSvcImage('DATA-CONTENT-IMAGE USER@CONSTELLATION.COM!PNG!/OPERATORIMAGES/')
   *       .then((data) => {
   *          const url = window.URL.createObjectURL(data);
   *          return url;
   *       })
   *       .catch(() => {
   *          console.error('Unable to load the image!');
   *       });
   * // returns -- Promise that resolves to the image (blob) specified as the key
   * @param key Identifier to the image
   * @returns Promise that resolves to the image (blob) specified as the key
   * @function
   */
  getSvcImage(key: string) {
    /*
     * Commented out the following call to serviceBrokerFetch() due to BUG-635925. Also note that ServiceBrokerFetch seems to
     * rely on "context" and "pzctkn" request headers when called from Infinity (see comment in httpmain.js), yet don't appear necessary
     * when invoked from Constellation (i.e., UI Authoring). Return out the blob data.
     */

    const callUrl = `${this.appStaticUrl}v102/${this.appAlias}/image/${key}`;

    return fetch(callUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { Authorization: `Bearer ${this.b2sJWT}` }
    })
      .then((response) => response.blob())
      .catch();

    /*
        // Service broker manages injection of B2S JWT into headers and maintenance of current JWT
        return serviceBrokerFetch(callUrl, {
            method: 'GET',
            credentials: 'include',
            headers: { Authorization: `Bearer ${this.b2sJWT}` }
        });
*/
  }

  /**
   * Fetches an image as a blob URL from the Constellation service. This call is authenticated
   * using the token issued for the Constellation service (B2S JWT).
   *
   * @param {string} key Identifier for the image.
   * @returns {Promise<string | Blob>} Promise that resolves to the image blob URL.
   */
  getSvcImageUrl(key: string): Promise<string | Blob> {
    const infinityStaticService = EnvironmentInfo.environmentInfoObject?.pyInfinityStaticService;

    const { callUrlKey, queryPayload, callUrl } = this.buildImageRequest(key, infinityStaticService);

    if (!this.imageBlobUrlCache[callUrl]) {
      this.imageBlobUrlCache[callUrl] = this.fetchImage(callUrlKey, queryPayload, callUrl, infinityStaticService);
    }

    return this.imageBlobUrlCache[callUrl];
  }

  /**
   * Builds the request details for fetching an image.
   *
   * @param {string} key Identifier for the image.
   * @param {boolean} infinityStaticService Flag indicating if the Infinity Static Service is available.
   * @returns {object} Object containing the callUrlKey, queryPayload, and callUrl.
   * @private
   */
  buildImageRequest(key: string, infinityStaticService: string | undefined) {
    let callUrlKey = GET_BINARY_FILE;
    let queryPayload;
    let callUrl = `${this.appStaticUrl}v102/${this.appAlias}/image/${key}`;

    if (infinityStaticService) {
      const regex = /DATA-CONTENT-IMAGE\s([^\s!]+)!.*\/OPERATORIMAGES\//i;
      const match = key.match(regex);

      if (match && match[1]) {
        callUrlKey = GET_OPERATOR_IMAGE;
        queryPayload = { user_ID: match[1].toLowerCase() };
      } else {
        queryPayload = { fileID: key };
      }

      callUrl = resolveRestApi(callUrlKey, queryPayload)?.restApi || callUrl;
    }

    return { callUrlKey, queryPayload, callUrl };
  }

  /**
   * Fetches the image and converts it to a blob URL.
   *
   * @param {string} callUrlKey API key for the request.
   * @param {object | null} queryPayload Payload for the API request.
   * @param {string} callUrl URL for the API request.
   * @returns {Promise<string>} Promise that resolves to the image blob URL.
   * @private
   */
  fetchImage(
    callUrlKey: string,
    queryPayload: BuildRestAPIPayload | undefined,
    callUrl: string,
    infinityStaticService: string | undefined
  ): Promise<string> {
    if (infinityStaticService) {
      return invokeRestApi(callUrlKey, { queryPayload })
        .then((response) => {
          const byteArray = this.decodeBase64ToByteArray(response.data);
          const mimeType = this.getMimeTypeFromKey(queryPayload?.fileID || queryPayload?.user_ID || '');
          const blob = new Blob([byteArray], { type: mimeType });
          return window.URL.createObjectURL(blob); // Create a Blob URL
        })
        .catch((error) => {
          console.error('Error fetching image:', error);
          throw error;
        });
    }

    return fetch(callUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { Authorization: `Bearer ${this.b2sJWT}` }
    })
      .then((response) => response.blob())
      .then((blob) => {
        return window.URL.createObjectURL(blob);
      })
      .catch();
  }

  /**
   * Determines the MIME type for Pega format files.
   * Handles formats like: "WEBWB!APPLICATIONLOGO20251104T150002.315%20GMT!SVG"
   *
   * @param {string} fileKey The file identifier or path.
   * @returns {string} The appropriate MIME type.
   * @private
   */
  getMimeTypeFromKey(fileKey: string): string {
    const key = fileKey.toLowerCase();

    // Quick lookup instead of regex and multiple ifs
    const mimeMap: Record<string, string> = {
      svg: 'image/svg+xml',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif'
    };

    // Find last "!" and take text after it
    const ext = key.split('!').pop() || '';
    if (mimeMap[ext]) return mimeMap[ext];

    return 'application/octet-stream';
  }

  /**
   * Decodes a base64 string into a Uint8Array.
   *
   * @param {string} base64String Base64 encoded string.
   * @returns {Uint8Array} Decoded byte array.
   * @private
   */
  decodeBase64ToByteArray(base64String: string): Uint8Array {
    const byteCharacters = atob(base64String);
    const len = byteCharacters.length;
    const bytes = new Uint8Array(len);
    for (let index = 0; index < len; index += 1) {
      bytes[index] = byteCharacters.charCodeAt(index);
    }
    return bytes;
  }

  /**
   * This API gets the localization instance from the constellation service for a given user locale and an instance key. This call is authenticated
   * by the service, using the token issued for the Constellation service (B2S JWT).
   * @example <caption>Example for getSvcLocale('en_US','WORK-!VIEW!PYCASESUMMARY.json')</caption>
   * // In this example, the API gets the localeJson for the en_US locale and for the view PyCaseSummary in the Work- class.
   * // This API call returns a Promise which when resolved gives the localeJson.
   * const localeJson = await AssetLoader.getSvcLocale('en_US', 'WORK-!VIEW!PYCASESUMMARY.json')
   *       .then((data) => {
   *          // data is the response from the constellation service which is the localeJson
   *       });
   * @param locale     locale for which we need locale JSON
   * @param key        Identifier to the JSON files
   * @returns Promise that resolves to a localeJson
   * @function
   */
  getSvcLocale(locale: string | unknown, key: string) {
    const callUrl = `${this.appStaticUrl}v102/${this.appAlias}/locales/${locale}/${key}`;

    return fetch(callUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { Authorization: `Bearer ${this.b2sJWT}` }
    });
  }

  /**
   * This API gets all locale codes available in app-static for a given application.
   * This call is authenticated by the service, using the token issued for the Constellation service (B2S JWT).
   * @example <caption>Example for getSvcAvailableLocales()</caption>
   * // This API call returns a Promise which when resolved gives the available locale codes in Constellation service.
   * @returns Promise that resolves to list of strings of available locales
   * @function
   * @private
   */
  getSvcAvailableLocales(): Promise<string[]> {
    const callUrl = `${this.appStaticUrl}v102/app/${this.appAlias}/locales/getLocaleCodes`;
    return fetch(callUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { Authorization: `Bearer ${this.b2sJWT}` }
    })
      .then((response) => response.json())
      .catch(() => {
        return [];
      });
  }

  /**
   * This exposes a method to call the Constellation service to fetch a custom component. This call is authenticated
   * by the service, using the token issued for the Constellation service (B2S JWT).
   * @example <caption>Example for getSvcComponent()</caption>
   * // In this example, the API retrieves the JS implementation for the WeightInput component.
   * const WeightInputDefinition = await AssetLoader
   *      .getSvcComponent('WeightInput.js')
   *      .then((response) => {
   *         return response;
   *       })
   *       .catch(() => {
   *         console.error('Unable to retrieve the component!');
   *       });
   * // returns -- Promise that resolves to the corresponding JS implementation for the component
   * @param name Name of the component whose JS implementation we want to fetch
   * @returns Promise that resolves to the corresponding JS implementation for the component
   * @function
   */

  async getSvcComponent(name: string) {
    try {
      const infinityStaticService = EnvironmentInfo.environmentInfoObject?.pyInfinityStaticService;

      if (infinityStaticService) {
        const updateDateTime = EnvironmentInfo.environmentInfoObject?.pxApplication?.pxUpdateDateTime;
        const timestamp = updateDateTime ? Utils.parsePegaDateTimeToTimestamp(updateDateTime) : null;
        const componentName = `${name}${timestamp ? `?v=${timestamp}` : ''}`;
        const response = await invokeRestApi(GET_CUSTOM_COMPONENT, {
          queryPayload: { componentName }
        });
        return response.data;
      }

      const devServerURL = EnvironmentInfo.environmentInfoObject?.pyC11nDXCBDevURL;

      const callUrl = devServerURL
        ? `${devServerURL}/index.js`
        : `${this.appStaticUrl}v102/${this.appAlias}/component/${name}`;

      const response = await fetch(
        callUrl,
        devServerURL
          ? {}
          : {
              method: 'GET',
              credentials: 'include',
              headers: { Authorization: `Bearer ${this.b2sJWT}` }
            }
      );
      if (!response.ok) throw new Error(response.statusText);

      return await response.text();
    } catch (error) {
      console.error('Error fetching service component:', error);
      throw error;
    }
  }

  /**
   * This exposes a method to call the Constellation service to fetch a custom components library map. This call is authenticated
   * by the service, using the token issued for the Constellation service (B2S JWT).
   * @example <caption>Example for importCustomComponentMap()</caption>
   * Example can be seen in bootstraping.
   * @param libId Custom Component Design Library Id and Version
   * @function
   * @private
   */
  async importCustomComponentMap(libId: string) {
    try {
      const infinityStaticService = EnvironmentInfo.environmentInfoObject?.pyInfinityStaticService;
      const devServerURL = EnvironmentInfo.environmentInfoObject?.pyC11nDXCBDevURL;
      let url = `${this.appStaticUrl}v100/componentlib/componentsmap/${libId}`;

      if (devServerURL) {
        const normalizedURL = devServerURL.replace(/\/+$/, '');
        url = `${normalizedURL}/index.js`;
      }
      if (infinityStaticService && !devServerURL) {
        const updateDateTime = EnvironmentInfo.environmentInfoObject?.pxApplication?.pxUpdateDateTime;
        const timestamp = updateDateTime ? Utils.parsePegaDateTimeToTimestamp(updateDateTime) : null;
        const componentName = `${libId}${timestamp ? `?v=${timestamp}` : ''}`;
        const response = await invokeRestApi(GET_CUSTOM_COMPONENT, {
          queryPayload: { componentName }
        });
        this.appendCustomScriptToHead(response.data);
        return;
      }

      if (infinityStaticService || devServerURL) {
        const element = document.createElement('script');
        element.src = url;
        element.type = 'text/javascript';
        element.onerror = () => console.error('Error while appending custom component map');
        document.head.appendChild(element);
        return;
      }

      const options = devServerURL ? {} : { headers: { Authorization: `Bearer ${this.b2sJWT}` } };
      const response = await fetch(url, options);

      if (!response.ok) {
        console.error('Error while fetching custom component map');
      }

      const data = await response.text();
      this.setCCV2LibId(libId);

      this.appendCustomScriptToHead(data);
    } catch (ex) {
      console.error('Exception while appending custom component map:', ex);
    }
  }

  async appendCustomScriptToHead(data: string) {
    try {
      const element = document.createElement('script');
      element.src = window.URL.createObjectURL(new Blob([data], { type: TEXT_JAVASCRIPT }));
      element.onerror = () => console.error('Error while appending custom component map');

      document.head.appendChild(element);
    } catch (error) {
      console.error('Exception appendCustomScriptToHead:', error);
    }
  }

  /**
   * This exposes a method to call the Constellation service to fetch a custom component and attach the component script in document HEAD. This call is authenticated
   * by the service, using the token issued for the Constellation service (B2S JWT).
   * @example <caption>Example for loadSvcComponent()</caption>
   * // In this example, the API retrieves the JS implementation for the WeightInput component and attached to document HEAD in a <script /> tag.
   * const WeightInputDefinition = await AssetLoader
   *      .loadSvcComponent('WeightInput')
   *      .then((componentName) => {
   *         return componentName;
   *       })
   *       .catch(() => {
   *         console.error('Unable to retrieve the component!');
   *       });
   * // returns -- Promise that resolves to the corresponding JS implementation for the component
   * @param name Name of the component whose JS implementation we want to fetch
   * @returns Promise that resolves to the component name
   * @function
   */

  loadSvcComponent(name: string) {
    return new Promise((resolve, reject) => {
      this.getSvcComponent(name)
        .then((data) => {
          const element = document.createElement('script');
          element.src = window.URL.createObjectURL(new Blob([data], { type: TEXT_JAVASCRIPT }));
          element.onload = () => resolve(name);
          element.onerror = () => reject(name);
          document.head.appendChild(element);
        })
        .catch(() => {
          reject(name);
        });
    });
  }

  /*
        // Service broker manages injection of B2S JWT into headers and maintenance of current JWT
        return serviceBrokerFetch(callUrl, {
            method: 'GET',
            credentials: 'include',
            headers: { Authorization: `Bearer ${this.b2sJWT}` }
        });
*/

  /**
   * Sets the urls to fetch pega static content and customer static content.
   * @description This sets static server urls as passed in its argument
   *
   * @example <caption>Example for initServer()</caption>
   * Example can be seen in loadLibs,setStaticServerUrl methods in ui_bootstrap
   * Example usage - AssetLoader.initServer('https://mirror.pegacloud.net/prweb')
   * This initiates server with url - https://mirror.pegacloud.net/prweb as static content url
   *
   * @param url url to fetch pega static content
   * @param appUrl Service url to fetch app static content
   * @param b2sJWT Authorization header token (optional)
   * This is used to access protected resources

   * @see {@link setStaticServerUrl} for further information.
   * @function
   */
  initServer(url: string, appUrl: string | null, b2sJWT: string) {
    /*
      UC 1: CDN + app-static service
      UC 2: CDN only
      UC 3: service only
                  url      appUrl
      UC1          x          x
      UC2          x       'no-appstatic'
      UC3          x       'no-cdn'
    */

    console.log(`asset-loader.initServer(${url}, ${appUrl})`);

    // init can be called multiple times; must ensure coherent settings
    this.appStaticUrl = null;
    this.staticServerURL = null;
    this.pegaOnlyUrl = false;
    this.componentsConfig = null;

    // uc1, cdn+service
    if (url && appUrl && appUrl !== 'no-appstatic' && appUrl !== 'no-cdn') {
      this.appStaticUrl = appUrl.endsWith('/') ? appUrl : `${appUrl}/`;
      this.staticServerURL = url.endsWith('/') ? url : `${url}/`;
      this.pegaOnlyUrl = true;
    }
    // uc2, cdn only
    else if (url && appUrl && appUrl === 'no-appstatic') {
      this.staticServerURL = url.endsWith('/') ? url : `${url}/`;
      this.appStaticUrl = this.staticServerURL; // added to support below getImage, getComponent, getLocale calls
      this.pegaOnlyUrl = true;
    }
    // uc3, service only
    else if (url && appUrl && appUrl === 'no-cdn') {
      this.staticServerURL = url.endsWith('/') ? url : `${url}/`;
      this.appStaticUrl = this.staticServerURL; // added to support below getImage, getComponent, getLocale calls
    }

    console.log(
      `asset-loader.initServer() staticServerURL=${this.staticServerURL}, appStaticUrl=${this.appStaticUrl}, pegaOnlyUrl=${this.pegaOnlyUrl})`
    );

    this.b2sJWT = b2sJWT;
  }

  /**
   * Registers the custom loader to the Asset Loader Class.
   * @description register custom loader to the Asset Loader Class.
   * This can be used to register custom loaders.
   *
   * @example <caption>Example for register() depicting how to register
   * a custom loader viz. font-loader to use loadWebFonts function </caption>
   * Example usage - AssetLoader.register('font-loader', loadWebFonts)
   *
   * @param name name of the loader like Font-Loader etc.,
   * @param loaderFn loader function, function
   * must return Promise with assets.
   *
   * @function
   */
  register(name: string, loaderFn: Function) {
    this.loaderObj[name] = loaderFn;
  }

  /**
   * Returns the loader associated/registered with loader name.
   * @description returns custom loader if defined else defaults to default loader.
   *
   * @example <caption>Example for register()</caption>
   * Example usage - AssetLoader.getLoader('font-loader')
   * which returns the loadWebFonts method/API
   *
   * Example can be seen in component-loader loadComponents method
   * @param name name of the loader like Font-Loader etc.,
   *
   * @returns returns the loader
   * @default defaults to in-build js/css loader
   *
   * @see {@link loadComponents} for example usage.
   *
   * @function
   */
  getLoader(name?: string): Function {
    let loader;
    const defaultLoader = (url: string, type: string) => {
      /* TODO: Asynchronously load CSS  */
      return new Promise((resolve, reject) => {
        const element = document.createElement(type);
        // Need to set different attributes depending on tag type
        if (type === 'script') {
          const scriptElement = element as HTMLScriptElement;
          // Important success and error for the promise
          scriptElement.onload = () => {
            resolve(url);
          };
          scriptElement.onerror = () => {
            reject(url);
          };
          scriptElement.type = TEXT_JAVASCRIPT;
          scriptElement.charset = 'UTF-8';
          scriptElement.async = false;
          scriptElement.src = url;
        } else {
          const linkElement = element as HTMLLinkElement;
          linkElement.type = 'text/css';
          linkElement.rel = 'stylesheet';
          linkElement.href = url;
        }
        // Inject into document to kick off loading
        document.head.appendChild(element);
      }).catch((e) => {
        console.log(e);
      });
    };
    if (name) {
      loader = this.loaderObj[name];
    } else {
      loader = defaultLoader;
    }
    return loader;
  }

  /**
   * Loads the assets from asset dependencies list.
   * @description loads asset dependencies as individual promises
   * @param options
   * It reads the dependency from assets map and process the assets mapped in it.
   * Once all scripts loaded successfully through Promise.all, it will call render.
   *
   * @example <caption>Example for loadAssets()</caption>
   * Example usage - AssetLoader.loadAssets('https://mirror.pegacloud.net/assets/react.prod.js')
   * which loads the react prod file into html
   *
   * Example can be seen in history-epic showHistory method
   * and processViewResponse method in util
   *
   * @param assets assets list eq., section...
   * @returns promise Object for the resolved Assets
   *
   * @see {@link processViewResponse} for further information.
   * @see {@link showHistory} for example usage.
   *
   * @function
   */
  loadAssets(assets: Asset[], options: LoadAssetOptions) {
    const { isDevServerMode } = options;
    if (!assets) {
      return Promise.resolve();
    }

    const assetPromises: Promise<string | undefined>[] = [];
    assets.forEach((assetItem: object) => {
      const name = Object.keys(assetItem)[0];
      let url = isDevServerMode ? Object.values(assetItem)[0] : this.getStaticServerUrl();
      url = url.endsWith('/') ? url : `${url}/`;
      /* To avoid loading multiple times */
      if (this.loadedAssets[name]) {
        return;
      }
      if (name.includes('.js')) {
        assetPromises.push(this.getLoader()(url + name, this.getTypes.SCRIPT));
      } else if (name.includes('.css')) {
        this.getLoader()(url + name, this.getTypes.STYLE);
      }
      this.loadedAssets[name] = true;
    });
    return Promise.all(assetPromises);
  }

  /**
   * This exposes a method to call the Constellation app static service to fetch assets from isolations. This call is authenticated
   * by the service, using the token issued for the Constellation service (B2S JWT).
   * @example <caption>Example for getAssetsFromIsolations()</caption>
   * // In this example, the API retrieves logo image and creates a DOMString containing a URL representing the image Blob object.
   * const operatorImg = await AssetLoader
   *       .getAssetsFromIsolations('image/sample.png')
   *       .then((data) => {
   *          const url = window.URL.createObjectURL(data);
   *          return url;
   *       })
   *       .catch(() => {
   *          console.error('Unable to load the image!');
   *       });
   * // returns -- Promise that resolves to the image (blob) specified as the key
   * @param assetPath path to the image
   * @returns Promise that resolves to the image (blob) specified as the key
   * @function
   * @private
   */
  getAssetsFromIsolations(assetPath: string) {
    const base64 = this.b2sJWT?.split('.')[1].replace(/-/g, '+').replace(/_/g, '/') || '';
    const jsonPayload = decodeURIComponent(
      global
        .atob(base64)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    const jsonObj = JSON.parse(jsonPayload);
    const isolationID = jsonObj?.tenant_id;
    const { restApi, reqType } =
      getRestEndPoint('getAssetsFromIsolations', {
        isolationID,
        assetPath
      }) || {};
    const callUrl = `${this.appStaticUrl}${restApi}`;
    return fetch(callUrl, {
      method: reqType,
      credentials: 'include',
      headers: { Authorization: `Bearer ${this.b2sJWT}` }
    }).then((response) => response.blob());
  }
}

export default new AssetLoader();
